import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  disableTerminalPairing,
  enableTerminalPairing,
  kioskCommand,
  resetTerminalPairing
} from 'api/Kiosk';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Button/Button';
import Input from 'components/shared/Input/Input';
import Modal from 'components/shared/Modal/Modal';

const DebugActions = ({ kiosk }) => {
  const [commandValue, setCommandValue] = useState('');
  const normalizedCommand = commandValue?.trim();
  const [openCommandModal, setOpenCommandModal] = useState(false);
  const navigate = useNavigate();

  const { mutate: enableTerminal } = useCustomMutation({
    mutationFn: () => enableTerminalPairing(navigate, { kioskId: kiosk.id }),
    successMessage: 'Terminal pairing enabled successfully',
    invalidateQueryKey: ['kiosks'],
    errorMessage: 'There was a problem enabling terminal pairing'
  });

  const { mutate: resetTerminal } = useCustomMutation({
    mutationFn: () => resetTerminalPairing(navigate, { kioskId: kiosk.id }),
    successMessage: 'Payment terminal reset successfully',
    invalidateQueryKey: ['kiosks'],
    errorMessage: 'There was a problem resetting the payment terminal'
  });

  const { mutate: disableTerminal } = useCustomMutation({
    mutationFn: () => disableTerminalPairing(navigate, { kioskId: kiosk.id }),
    successMessage: 'Terminal pairing disabled successfully',
    invalidateQueryKey: ['kiosks'],
    errorMessage: 'There was a problem disabling terminal pairing'
  });

  const { mutateAsync: sendKioskCommand, isPending: loading } = useCustomMutation({
    mutationFn: (values) => kioskCommand(navigate, values),
    successMessage: 'Kiosk command sent successfully',
    invalidateQueryKey: ['kiosks'],
    errorMessage: 'There was a problem sending the kiosk command'
  });

  return (
    <>
      <div className="flex flex-col gap-2 px-5">
        <p className="font-500 text-neutral-900">Kiosk debugging actions</p>
        <p className="text-sm text-neutral-700">
          When the terminal is not connected, please enable the terminal pairing mode. It'll show a
          small popup on the bottom of the screen with an option to pair a terminal (Moby/5500).
        </p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 px-5">
        <Button
          variant="tertiary"
          size="small"
          text="Enable terminal pairing"
          onClick={enableTerminal}
          icon="tick-circle-mono"
          iconColor="neutral"
        />
        <Button
          variant="tertiary"
          size="small"
          icon="minus-circle-mono"
          text="Disable terminal pairing"
          onClick={disableTerminal}
          iconColor="neutral"
        />
        <Button
          variant="tertiary"
          size="small"
          text="Kiosk validation"
          onClick={() =>
            sendKioskCommand({
              command: 'url-change',
              kioskId: kiosk?.id,
              url: '/kiosk/validation-page'
            })
          }
          icon="checkbox-circle-mono"
          iconColor="neutral"
        />
        <Button
          variant="tertiary"
          size="small"
          text="Reset payment terminal"
          onClick={resetTerminal}
          icon="rotate-ccw-mono"
          iconColor="neutral"
        />
        <Button
          variant="tertiary"
          size="small"
          onClick={() => setOpenCommandModal(true)}
          text="Send kiosk command"
          icon="code-2-mono"
          iconColor="neutral"
        />
      </div>
      <Modal
        className="max-h-[250px]"
        width="medium"
        slideFromBottom
        overlayClassName="items-end"
        title="Send command to kiosk for debugging purposes"
        handleClose={() => setOpenCommandModal(false)}
        handleOpen={openCommandModal}
        bodyClassName="h-fit"
        footer={
          <div className="flex w-full justify-end gap-4">
            <Button
              text="Cancel"
              variant="tertiary"
              onClick={() => setOpenCommandModal(false)}
              size="small"
            />
            <Button
              isDisabled={loading || normalizedCommand === ''}
              text="Send kiosk command"
              onClick={() => sendKioskCommand({ command: normalizedCommand, kioskId: kiosk?.id })}
              color="primary"
              size="small"
            />
          </div>
        }>
        <Input
          label="Kiosk Command"
          value={commandValue}
          onChange={(e) => setCommandValue(e.target.value)}
        />
      </Modal>
    </>
  );
};

export default DebugActions;
